import { ApiEndpoint, ApiGroup } from '@/types/api';

/**
 * Groups API endpoints by their type
 * @param apis Array of API endpoints to group
 * @returns Array of API groups
 */
export const groupApisByType = (apis: ApiEndpoint[]): ApiGroup[] => {
  const groupMap = new Map<string, ApiEndpoint[]>();
  const typeOrder = new Map<string, number>();
  
  // Group APIs by type and track the order of first occurrence
  apis.forEach((api, index) => {
    const { type } = api;
    if (!groupMap.has(type)) {
      groupMap.set(type, []);
      typeOrder.set(type, index); // Remember when this type first appeared
    }
    groupMap.get(type)!.push(api);
  });
  
  // Convert map to array and maintain original order from JSON
  return Array.from(groupMap.entries())
    .map(([type, groupApis]) => ({
      type,
      displayName: formatGroupName(type),
      description: `APIs related to ${formatGroupName(type).toLowerCase()}`,
      apis: groupApis,
      count: groupApis.length,
      originalOrder: typeOrder.get(type)!
    }))
    .sort((a, b) => a.originalOrder - b.originalOrder)
    .map(({ originalOrder, ...group }) => group); // Remove the temporary originalOrder property
};


/**
 * Formats group name to be more readable
 * @param name The raw group name
 * @returns Formatted group name
 */
const formatGroupName = (name: string): string => {
  // Special cases for specific types
  const specialNames: Record<string, string> = {
    'authentication': 'Authentication',
    'user-preferences': 'User Preferences and Settings',
    'team-management': 'Team Management APIs',
    'user-activity': 'User Activity and Analytics',
    'user-operations': 'User Operations',
    'graph-management': 'Graph Management',
    'asset-management': 'Asset Management',
    'schema-management': 'Schema Management',
    'bulk-management': 'Bulk Operations',
    'tag-management': 'Tag Management',
    'webhook-management': 'Webhook Management',
    'owner-management': 'Owner Management',
    'space-allocation': 'Space Allocation',
    'visitor-allocation': 'Visitor Allocation',
    'amenity-booking': 'Amenity Booking',
    'meeting-booking': 'Meeting Booking',
    'resource-discovery': 'Resource Discovery',
    'location-data': 'Location Data',
    'occupancy-data': 'Occupancy Data',
    'occupancy-historic': 'Occupancy Historic',
    'policy-historic': 'Policy Historic',
    'environmental-historic': 'Environmental Historic',
    'flow-historic': 'Flow Historic',
    'spotdata-historic': 'Spotdata Historic',
    'cleaning-historic': 'Cleaning Historic',
    'demand-historic': 'Demand Historic',
    'social-distance-historic': 'Social Distance Historic',
    'graphql-historic': 'GraphQL Historic',
    'space-management-historic': 'Space Management Historic',
    'report-historic': 'Report Historic',
    'filter-historic': 'Filter Historic',
    // Node Spot API specific types
    'authentication-spot': 'Authentication',
    'asset': 'Asset',
    'real-time-asset': 'Real Time Asset', 
    'historic': 'Historic',
    'graph': 'Graph',
    'node': 'Node',
    'schema': 'Schema',
    'bulk': 'Bulk',
    'tag': 'Tag'
  };
  
  return specialNames[name] || name
    .split(/[-_\s]+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const filterApis = (apis: ApiEndpoint[], query: string): ApiEndpoint[] => {
  if (!query.trim()) {
    return apis;
  }

  const lowercaseQuery = query.toLowerCase();
  
  return apis.filter((api) => {
    const searchableText = [
      api.name,
      api.description,
      api.path,
      api.method,
      ...(api.tags || [])
    ].join(' ').toLowerCase();
    
    return searchableText.includes(lowercaseQuery);
  });
};